import { useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { AuthProvider, AuthGate, useAuth } from "./features/auth";
import { WellnessProvider } from "./context/WellnessContext";
import AnimatedRoutes from "./components/AnimatedRoutes";
import { BootstrapProvider, useBootstrap } from "./features/bootstrap";
import { useLoadAllWellnessData } from "./hooks/useLoadAllWellnessData";
import * as Sentry from "@sentry/react";
import { shouldInitSentry } from "./utils/sentryConfig";
import { useStudentAnalyticsAggregation } from "./hooks/useStudentAnalyticsAggregation";
import { useDataLoadTiming } from "./hooks/useDataLoadTiming";
import { initializeAnalytics } from "./utils/analytics";
import { trackProductEvent } from "./utils/analytics/track";
import { canTrackMixpanel } from "./utils/analytics/consent";
import DisclaimerScreen from "./screens/DisclaimerScreen/DisclaimerScreen";
import { StudentDataProvider } from "./context/studentData.context";
import { StudentSupportProvider } from "./context/studentSupport.context";
import { MyStoryProvider } from "./context/myStory.context";
import { CleverCallback } from "./features/auth";

function AppInner() {
  const { user } = useAuth();
  const { data: userBootstrapData } = useBootstrap();
  const didTrackLoginRef = useRef(false);

  useEffect(() => {
    if (shouldInitSentry && user?._id) {
      Sentry.setUser({
        id: user._id,
        userType: user.role,
      });
    }
  }, [user]);

  useLoadAllWellnessData();
  useStudentAnalyticsAggregation();
  useDataLoadTiming();

  useEffect(() => {
    initializeAnalytics({
      token: process.env.REACT_APP_MIXPANEL_TOKEN,
      user,
    });
  }, [user]);

  useEffect(() => {
    if (!user?._id || didTrackLoginRef.current) return;
    trackProductEvent(
      "funnel_step",
      {
        funnel: "onboarding",
        step: "login_success",
        page: window.location.pathname,
        role: user.role,
      },
      user,
      { allowWithoutConsent: true },
    );
    didTrackLoginRef.current = true;
  }, [user]);

  useEffect(() => {
    if (!user?._id) return;
    const pending = window.__demoAuthPending;
    if (!pending || window.__demoAuthSuccessTracked) return;
    const durationMs =
      pending.startedAt !== undefined
        ? Math.max(0, Date.now() - pending.startedAt)
        : undefined;
    trackProductEvent(
      "demo_signup_auth_success",
      {
        page: window.location.pathname,
        method: pending.method || "unknown",
        duration_ms: durationMs,
      },
      user,
      { allowWithoutConsent: true },
    );
    window.__demoAuthSuccessTracked = true;
    window.__demoAuthPending = null;
  }, [user]);

  useEffect(() => {
    if (!userBootstrapData?.studentIds?.length) return;
    if (!window.__dataLoadStart) {
      window.__dataLoadStart = performance.now();
    }
  }, [userBootstrapData]);

  useEffect(() => {
    if (!canTrackMixpanel(user)) return;

    const onError = () => {
      trackProductEvent(
        "error_event",
        {
          error_type: "window_error",
          page: window.location.pathname,
          role: user?.role,
        },
        user,
      );
    };

    const onUnhandledRejection = () => {
      trackProductEvent(
        "error_event",
        {
          error_type: "unhandled_rejection",
          page: window.location.pathname,
          role: user?.role,
        },
        user,
      );
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [user]);

  return (
    <>
      {user && !user.agreedToTerms && (
        <Routes>
          <Route path="*" element={<DisclaimerScreen user={user} />} />
        </Routes>
      )}
      {user && user.agreedToTerms && (
        <StudentDataProvider>
          <StudentSupportProvider>
            <MyStoryProvider>
              <Routes>
                <Route path="/*" element={<AnimatedRoutes />} />
              </Routes>
            </MyStoryProvider>
          </StudentSupportProvider>
        </StudentDataProvider>
      )}
    </>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <AuthProvider>
          <CleverCallback />
          <AuthGate>
            <BootstrapProvider>
              <WellnessProvider>
                <AppInner />
              </WellnessProvider>
            </BootstrapProvider>
          </AuthGate>
        </AuthProvider>
        <ToastContainer
          position="top-right"
          className="custom-toast-container"
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </Router>
  );
}

export default App;
